import type { ChatSuggestionSurface } from '@server/analytics-events';
import { useEffect } from 'react';

import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { cn } from '@/_shared/lib/utils';

import { trackSuggestionShownOnce } from '../lib/suggestion-analytics';

interface Action {
  label: string;
  message?: string;
}

interface RecommendedActionsProps {
  content: string;
  metadata?: unknown;
  onAction?: (message: string) => void;
  disabled?: boolean;
  /** Hide save-dashboard suggestions when the preview is already persisted. */
  dashboardSaved?: boolean;
  /** The chat surface, for suggestion analytics. Omitted, nothing is tracked. */
  surface?: ChatSuggestionSurface;
  /** The turn whose reply carried these actions. */
  turnId?: string;
  /** The rETL connection the chat is about (the Ask why chat), so its actions split by connection. */
  rsConnectionId?: string;
  /**
   * Rows inside a block that owns the frame and the title (`NextStepsBlock`, under an assist card):
   * the actions alone, with no border box or title of their own.
   */
  embedded?: boolean;
  /** Labels the surrounding block already shows another way (an assist card saying the same); not drawn. */
  isDuplicate?: (label: string) => boolean;
}

/** LLM-generated labels vary; match only genuine "save this as a dashboard" call-to-actions. */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper for MessageList filtering and tests
export function isSaveDashboardSuggestion(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  // Anchor "save" to the START of the suggestion so only actual save CTAs are hidden — exploratory
  // items that merely mention a dashboard ("Add a funnel panel to the dashboard", "Compare dashboard
  // metrics") must survive, and "saved" ("view the saved dashboard") must not match.
  return (/^save\b/.test(normalized) && normalized.includes('dashboard')) || normalized.includes('save_dashboard');
}

function isSaveDashboardAction(action: Action): boolean {
  return (
    isSaveDashboardSuggestion(action.label) ||
    (action.message !== undefined && isSaveDashboardSuggestion(action.message))
  );
}

/** The block's title ("Next steps"), from the ui_message metadata. */
// eslint-disable-next-line react-refresh/only-export-components -- shared with NextStepsBlock, which draws the title
export function recommendedActionsTitle(metadata: unknown): string | null {
  if (
    metadata &&
    typeof metadata === 'object' &&
    'title' in metadata &&
    typeof (metadata as Record<string, unknown>)['title'] === 'string'
  ) {
    return (metadata as Record<string, unknown>)['title'] as string;
  }
  return null;
}

function parseActions(content: string): Action[] {
  try {
    const parsed: unknown = JSON.parse(content);
    if (Array.isArray(parsed)) {
      return parsed.map((item: unknown) => {
        if (typeof item === 'string') return { label: item };
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          return {
            label: (obj['label'] as string) ?? String((obj['message'] as string | number) ?? ''),
            message: obj['message'] as string | undefined,
          };
        }
        return { label: String(item) };
      });
    }
  } catch {
    return content
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((label) => ({ label }));
  }
  return [];
}

// eslint-disable-next-line react-refresh/only-export-components -- shared helper for MessageList filtering
export function getVisibleRecommendedActions(content: string, dashboardSaved = false): Action[] {
  return parseActions(content).filter((action) => !(dashboardSaved && isSaveDashboardAction(action)));
}

export function RecommendedActions({
  content,
  metadata,
  onAction,
  disabled,
  dashboardSaved = false,
  surface,
  turnId,
  rsConnectionId,
  embedded = false,
  isDuplicate,
}: RecommendedActionsProps): React.ReactElement | null {
  const actions = getVisibleRecommendedActions(content, dashboardSaved).filter(
    (action) => !isDuplicate?.(action.label),
  );
  const count = actions.length;

  useEffect(() => {
    // Not while the actions are disabled (a turn still recovering): they are on screen but cannot be
    // clicked yet. The set is reported once they can, like the routine starters.
    if (!surface || count === 0 || disabled) return;
    // Keyed on the content too: a turn without a turn_start (legacy rows) still reports each set once.
    trackSuggestionShownOnce(`${surface}:${turnId ?? ''}:${content}`, {
      surface,
      suggestionCount: count,
      turnId,
      ...(rsConnectionId ? { rsConnectionId } : {}),
    });
  }, [surface, turnId, rsConnectionId, content, count, disabled]);

  if (count === 0) return null;

  const title = recommendedActionsTitle(metadata);

  const rows = actions.map((action, i) => (
    <button
      key={i}
      disabled={disabled}
      onClick={() => {
        if (surface) {
          trackClientEvent(ANALYTICS_EVENTS.chatSuggestionClicked, {
            surface,
            position: i + 1,
            suggestionCount: count,
            turnId,
            ...(rsConnectionId ? { rsConnectionId } : {}),
          });
        }
        onAction?.(action.message ?? action.label);
      }}
      className={cn(
        'flex w-full items-center border-t border-border px-4 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground disabled:pointer-events-none disabled:opacity-50',
        embedded && 'first:border-t-0',
      )}
    >
      {action.label}
    </button>
  ));

  if (embedded) return <>{rows}</>;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="recommended-actions">
      {title && <NextStepsTitle>{title}</NextStepsTitle>}
      {rows}
    </div>
  );
}

/** The block's eyebrow title, shared with `NextStepsBlock`. */
export function NextStepsTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="px-4 py-3 text-xs font-semibold tracking-wider text-muted-foreground/60 uppercase">{children}</div>
  );
}
